import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Button,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { API_BASE_URL } from "../config";

// Same choices as the main app's subscription form.
const frequencies = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

// FastAPI errors: `detail` is a string, or a list of validation errors.
const errorMessage = (res) => {
  if (typeof res?.detail === "string") return res.detail;
  if (Array.isArray(res?.detail)) return res.detail.map((e) => e.msg).join("\n");
  return "Something went wrong. Please try again.";
};

const Subscription = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Set on /s/:code - a subscription referral link shared from the main app.
  const { code: referralCode } = useParams();
  const [referral, setReferral] = useState(null);
  const [referralError, setReferralError] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contact: "",
    whatsapp: "",
    amount: "",
    frequency: "monthly",
    institution_id: "",
    target_id: "",
    end_date: "",
    purpose: "",
  });

  useEffect(() => {
    const institution_id = searchParams.get("institution_id") || "";
    const target_id = searchParams.get("target_id") || "";
    if (institution_id || target_id) {
      setFormData((prev) => ({ ...prev, institution_id, target_id }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (!referralCode) return;
    fetch(`${API_BASE_URL}/api/v1/donation-referral/links/${encodeURIComponent(referralCode)}`)
      .then(async (r) => {
        const res = await r.json();
        // Organizer links refer donations only.
        if (!r.ok || res.data?.referrer_type !== "user") {
          throw new Error(r.ok ? "This subscription link is not valid." : errorMessage(res));
        }
        setReferral(res.data);
      })
      .catch((err) => setReferralError(err.message));
  }, [referralCode]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.email) newErrors.email = "Email is required";
    // PhoneInput values are digits with the country code, e.g. 919876543210.
    if (formData.contact.length < 10) newErrors.contact = "Contact is required";
    if (!formData.amount) newErrors.amount = "Amount is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    try {
      const requestBody = {
        donor: {
          name: formData.name,
          email: formData.email,
          contact: formData.contact,
          // PhoneInput can hold just the dial code ("91") once touched.
          whatsapp: formData.whatsapp.length >= 10 ? formData.whatsapp : formData.contact,
        },
        amount: Number(formData.amount),
        frequency: formData.frequency,
        institution_id: formData.institution_id ? Number(formData.institution_id) : null,
        target_id: formData.target_id ? Number(formData.target_id) : null,
        end_date: formData.end_date || null,
        notes: { purpose: formData.purpose || "General donation" },
        ...(referralCode ? { referral_code: referralCode } : {}),
      };

      const response = await fetch(`${API_BASE_URL}/api/v1/subscription/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      const res = await response.json();
      if (response.ok && res.short_url) {
        window.location.href = res.short_url;
      } else {
        alert(errorMessage(res));
      }
    } catch (error) {
      console.error("Subscription error:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-home">
      <div className="container-donation">
        <div className="donation-header">
          <span className="header-icon">🔄</span>
          {referral?.referrer_name && (
            <div className="referral-badge">Shared by {referral.referrer_name}</div>
          )}
          <h2>{referral?.institution?.name || "Create a Subscription"}</h2>
          <p>Set up recurring donations with ease</p>
        </div>

        {referralError ? (
          <div className="referral-error">
            <p>{referralError}</p>
            <Button variant="outlined" onClick={() => navigate("/subscription")}>
              Subscribe without the link
            </Button>
          </div>
        ) : (
        <form onSubmit={handleSubmit}>
          <Typography className="form-section-title">Donor Information</Typography>

          <TextField
            fullWidth variant="outlined" label="Full Name" required
            value={formData.name} onChange={handleChange("name")}
            error={!!errors.name} helperText={errors.name || ""}
            style={{ marginBottom: "15px" }}
          />

          <TextField
            fullWidth variant="outlined" label="Email" type="email" required
            value={formData.email} onChange={handleChange("email")}
            error={!!errors.email} helperText={errors.email || ""}
            style={{ marginBottom: "15px" }}
          />

          <div style={{ marginBottom: "15px" }}>
            <PhoneInput
              country="in"
              value={formData.contact}
              onChange={(value) => handleChange("contact")({ target: { value } })}
              specialLabel="Contact Number *"
              inputStyle={{ width: "100%", borderColor: errors.contact ? "#d32f2f" : undefined }}
              containerStyle={{ width: "100%" }}
            />
            {errors.contact && (
              <Typography variant="caption" color="error" style={{ marginLeft: 14 }}>
                {errors.contact}
              </Typography>
            )}
          </div>

          <div style={{ marginBottom: "28px" }}>
            <PhoneInput
              country="in"
              value={formData.whatsapp}
              onChange={(value) => handleChange("whatsapp")({ target: { value } })}
              specialLabel="WhatsApp Number (Optional)"
              inputStyle={{ width: "100%" }}
              containerStyle={{ width: "100%" }}
            />
          </div>

          <Typography className="form-section-title">Subscription Details</Typography>

          <TextField
            fullWidth variant="outlined" label="Amount (₹)" type="number" required
            value={formData.amount} onChange={handleChange("amount")}
            error={!!errors.amount} helperText={errors.amount || ""}
            style={{ marginBottom: "15px" }} inputProps={{ min: 1 }}
          />

          <TextField
            fullWidth select variant="outlined" label="Frequency"
            value={formData.frequency} onChange={handleChange("frequency")}
            style={{ marginBottom: "15px" }}
          >
            {frequencies.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth variant="outlined" label="End Date (Optional)" type="date"
            value={formData.end_date} onChange={handleChange("end_date")}
            style={{ marginBottom: "15px" }} InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth variant="outlined" label="Purpose / Notes (Optional)"
            multiline rows={3} value={formData.purpose} onChange={handleChange("purpose")}
            style={{ marginBottom: "24px" }}
          />

          <Button
            type="submit" variant="contained" fullWidth disabled={loading}
            style={{ padding: "13px", fontSize: "15px", fontWeight: 600 }}
          >
            {loading ? (
              <CircularProgress size={24} style={{ color: "white" }} />
            ) : (
              "Subscribe"
            )}
          </Button>
        </form>
        )}
      </div>
    </div>
  );
};

export default Subscription;
